'use client';

import { useEffect, useRef, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { Crown, Loader2, X } from 'lucide-react';
import Link from 'next/link';

import styles from '@/app/[locale]/ai-model/ai-models.module.css';
import { getAiChatCopy } from '@/libs/ai-chat-i18n';
import { getAiChatModelAccessCopy } from '@/libs/ai-chat-model-access-i18n';
import { getPricingCopy } from '@/libs/pricing-i18n';

type Props = {
  locale: string;
  modelName: string;
  plusMonthlyPriceUsd: number;
  proMonthlyPriceUsd: number;
  onClose: () => void;
  onNavigate: () => void;
};

export default function AiChatModelUpgradeDialog({ locale, modelName, plusMonthlyPriceUsd, proMonthlyPriceUsd, onClose, onNavigate }: Props) {
  const copy = getAiChatCopy(locale);
  const accessCopy = getAiChatModelAccessCopy(locale);
  const pricingCopy = getPricingCopy(locale).chat;
  const [loading, setLoading] = useState<'plus' | 'pro' | null>(null);
  const [error, setError] = useState('');
  const checkoutInFlight = useRef(false);
  const activeCheckout = useRef<AbortController | null>(null);
  const cancelButton = useRef<HTMLButtonElement>(null);
  const pricingPath = `${locale === 'en' ? '/pricing' : `/${locale}/pricing`}#ai-chat-plans`;

  useEffect(() => () => {
    activeCheckout.current?.abort();
    activeCheckout.current = null;
  }, []);

  function closeDialog() {
    activeCheckout.current?.abort();
    activeCheckout.current = null;
    onClose();
  }

  async function subscribe(plan: 'plus' | 'pro') {
    if (checkoutInFlight.current) return;
    checkoutInFlight.current = true;
    setLoading(plan);
    setError('');
    const controller = new AbortController();
    activeCheckout.current = controller;
    const timeout = window.setTimeout(() => controller.abort(), 20_000);
    try {
      const response = await fetch('/api/ai-chat/checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale, plan }),
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => null);
      if (activeCheckout.current !== controller) return;
      if (response.status === 401) {
        onNavigate();
        const returnTo = encodeURIComponent(pricingPath);
        window.location.assign(`${locale === 'en' ? '/signin' : `/${locale}/signin`}?returnTo=${returnTo}`);
        return;
      }
      if (!response.ok || !payload?.url) throw new Error(accessCopy.checkoutError);
      onNavigate();
      window.location.assign(payload.url);
    } catch {
      if (activeCheckout.current !== controller) return;
      checkoutInFlight.current = false;
      setLoading(null);
      setError(accessCopy.checkoutError);
    } finally {
      window.clearTimeout(timeout);
    }
  }

  return (
    <Dialog open onClose={closeDialog} initialFocus={cancelButton} className={styles.chatModelUpgradeRoot}>
      <div className={styles.chatModelUpgradeBackdrop} aria-hidden="true" />
      <div className={styles.chatModelUpgradeViewport}>
        <Dialog.Panel className={`${styles.chatUpgradeDialog} ${styles.chatModelUpgradeDialog}`}>
          <button className={styles.chatUpgradeClose} type="button" onClick={closeDialog} aria-label={copy.dismiss}><X aria-hidden="true" /></button>
          <span className={styles.chatModelPaidBadge}><Crown aria-hidden="true" />{accessCopy.paidBadge}</span>
          <Dialog.Title as="h2">{accessCopy.unlockTitle(modelName)}</Dialog.Title>
          <Dialog.Description>{accessCopy.unlockBody}</Dialog.Description>
          <div className={styles.chatModelUpgradePlans}>
            {(['plus', 'pro'] as const).map((plan) => (
              <section key={plan} aria-labelledby={`model-upgrade-${plan}`}>
                <h3 id={`model-upgrade-${plan}`}>{plan === 'plus' ? 'Plus' : 'Pro'}</h3>
                <p className={styles.chatModelUpgradePrice}>
                  <strong>${(plan === 'plus' ? plusMonthlyPriceUsd : proMonthlyPriceUsd).toFixed(1)}</strong>
                  <span>{pricingCopy.perMonth}</span>
                </p>
                <p>{plan === 'plus' ? pricingCopy.plusFeatures[0] : pricingCopy.proFeatures[0]}</p>
                <p>{plan === 'plus' ? pricingCopy.plusFeatures[2] : pricingCopy.proFeatures[1]}</p>
                <button type="button" onClick={() => void subscribe(plan)} disabled={loading !== null}>
                  {loading === plan && <Loader2 className="animate-spin" aria-hidden="true" />}
                  {loading === plan ? copy.subscribing : plan === 'plus' ? pricingCopy.getPlus : pricingCopy.getPro}
                </button>
              </section>
            ))}
          </div>
          {error && <p className={styles.chatModelUpgradeError} role="alert">{error}</p>}
          <div className={styles.chatModelUpgradeFooter}>
            <button ref={cancelButton} type="button" onClick={closeDialog}>{accessCopy.keepFree}</button>
            <Link href={pricingPath} onClick={onNavigate}>{accessCopy.viewPlans}</Link>
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}
